import React from 'react';
import { Search, X, ArrowUpDown, Check, ChevronDown } from 'lucide-react';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

const CAT_TABS = [
  { key: 'all', label: 'All' },
  { key: 'needs_review', label: 'Needs review' },
  { key: 'uncategorized', label: 'Uncategorized' },
  { key: 'categorized', label: 'Categorized' },
  { key: 'potential_assets', label: 'Potential assets' },
];

export const SORTS = [
  { key: 'confidence_asc', label: 'Lowest confidence' },
  { key: 'date_desc', label: 'Newest' },
  { key: 'date_asc', label: 'Oldest' },
  { key: 'amount_desc', label: 'Highest amount' },
];

export default function ReceiptFilters({
  query, setQuery,
  status, setStatus,
  categorization, setCategorization,
  needsReviewCount,
  sort, setSort,
  sortOpen, setSortOpen,
  colors,
}) {
  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-3 flex flex-col gap-3">
      {/* Search */}
      <div
        className="flex items-center gap-2 px-3 h-10 rounded-lg border"
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        data-testid="search-bar"
      >
        <Search size={16} style={{ color: colors.textMuted }} />
        <input
          data-testid="search-input"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search vendor or notes…"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-inherit"
          style={{ color: colors.text }}
        />
        {query && (
          <button data-testid="search-clear" onClick={() => setQuery('')}>
            <X size={16} style={{ color: colors.textMuted }} />
          </button>
        )}
      </div>

      {/* Status chips + Cat chips + Sort */}
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map((s) => (
          <button
            key={s.key}
            data-testid={`filter-${s.key}`}
            onClick={() => setStatus(s.key)}
            className="px-3 py-1.5 rounded-full border text-[13px] font-medium transition-colors"
            style={{
              backgroundColor: status === s.key ? colors.accentSoft : colors.surface,
              borderColor: status === s.key ? colors.accent : colors.border,
              color: status === s.key ? colors.accent : colors.text,
              fontWeight: status === s.key ? 700 : 500,
            }}
          >
            {s.label}
          </button>
        ))}

        <div className="w-px h-6 mx-1" style={{ backgroundColor: colors.border }} />

        {CAT_TABS.map((c) => {
          const isActive = categorization === c.key;
          const showBadge = c.key === 'needs_review' && needsReviewCount > 0;
          return (
            <button
              key={c.key}
              data-testid={`cat-filter-${c.key}`}
              onClick={() => setCategorization(c.key)}
              className="px-3 py-1.5 rounded-full border text-[13px] font-medium transition-colors"
              style={{
                backgroundColor: isActive ? colors.warningSoft : colors.surface,
                borderColor: isActive ? colors.warning : colors.border,
                color: isActive ? colors.warning : colors.textMuted,
                fontWeight: isActive ? 700 : 500,
              }}
            >
              {c.label}{showBadge ? ` (${needsReviewCount})` : ''}
            </button>
          );
        })}

        <div className="relative ml-auto">
          <button
            data-testid="sort-btn"
            onClick={() => setSortOpen((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[13px] font-medium transition-colors"
            style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.textMuted }}
          >
            <ArrowUpDown size={14} />
            Sort
            <ChevronDown size={12} />
          </button>

          {sortOpen && (
            <div
              className="absolute right-0 top-full mt-1 w-52 rounded-xl border shadow-lg z-20 overflow-hidden"
              style={{ backgroundColor: colors.surface, borderColor: colors.border }}
              data-testid="sort-menu"
            >
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  data-testid={`sort-${s.key}`}
                  onClick={() => { setSort(s.key); setSortOpen(false); }}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors hover:opacity-80"
                  style={{ color: sort === s.key ? colors.accent : colors.text }}
                >
                  <span style={{ fontWeight: sort === s.key ? 700 : 400 }}>{s.label}</span>
                  {sort === s.key && <Check size={16} style={{ color: colors.accent }} />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
